import * as React from "react";
import FilterContainer from "modules/common/components/filters/filterContainer";
import AudienceFilters from "../filter-components/audienceFilters";
import FilterSelection from "../filter-components/filterSelection";
import DateRangeSelectFilter from "../filter-components/dateRangeSelectFilter";
import CustomDateRangeFilter from "../filter-components/customDateRangeFilter";

import { dateOptions } from "utils/dateFormatting";
import { Button } from "@mui/material";
import { FILTER_BY_AUDIENCES, FILTER_BY_CUSTOM_AUDIENCES } from "utils/analyticsHelpers";
import moment, { Moment } from "moment";
import { Audience } from "modules/audiences";
import { useEffect } from "react";
import {
    AnalyticsReportFilters,
    AudienceStatsFilterv1,
    CUSTOM_RANGE,
    CustomRangeFilterOptions,
    FilterOptions,
    INCLUDE_12_MONTHS,
    INCLUDE_3_MONTHS,
    INCLUDE_6_MONTHS,
    LAST_12_MONTHS,
    LAST_3_MONTHS,
    LAST_6_MONTHS,
} from "../../models";

import "./../insightFilters.sass";

const AudienceAnalyticsFilterBar: React.FC<AudienceAnalyticsFilterBarProps> = (props) => {
    const [selectedValue, setSelectedValue] = React.useState<FilterOptions>(FilterOptions["Last 6 months"]);
    const [selectedCustomRangeValue, setSelectedCustomRangeValue] = React.useState<CustomRangeFilterOptions>(
        CustomRangeFilterOptions["3 months"]
    );
    const [customEnd, setCustomEnd] = React.useState<Moment | null>(moment(new Date()));
    const [audienceSearchText, setAudienceSearchText] = React.useState<string>("");
    const [audienceDropdownText, setAudienceDropdownText] = React.useState<string>(FILTER_BY_AUDIENCES);

    const filteredAudiences = React.useMemo(() => {
        return props.audiences.filter((audience) =>
            audience.displayName.toLowerCase().includes(audienceSearchText.toLowerCase())
        );
    }, [props.audiences, audienceSearchText]);

    const dateOptionsMenu = [
        { value: FilterOptions[LAST_3_MONTHS], label: LAST_3_MONTHS },
        { value: FilterOptions[LAST_6_MONTHS], label: LAST_6_MONTHS },
        { value: FilterOptions[LAST_12_MONTHS], label: LAST_12_MONTHS },
        { value: FilterOptions[CUSTOM_RANGE], label: CUSTOM_RANGE },
    ];

    const customDateOptionsMenu = [
        { value: CustomRangeFilterOptions[INCLUDE_3_MONTHS], label: `Include ${INCLUDE_3_MONTHS} up to` },
        { value: CustomRangeFilterOptions[INCLUDE_6_MONTHS], label: `Include ${INCLUDE_6_MONTHS} up to` },
        { value: CustomRangeFilterOptions[INCLUDE_12_MONTHS], label: `Include ${INCLUDE_12_MONTHS} up to` },
    ];

    const onChangeFilters = (filters: Partial<AnalyticsReportFilters>) => {
        props.onChangeFilters({ ...filters });
        setAudienceFilterText(props.filters.audiences);
        setAudienceSearchText("");
    };

    const onClearFilters = () => {
        const endDate = moment(new Date());
        const startDate = endDate.clone().startOf("day").subtract(5, "months");

        onToDateChange(endDate);
        setSelectedValue(FilterOptions["Last 6 months"]);

        onChangeFilters({
            startDate: startDate.format(dateOptions.basicWithHours),
            endDate: endDate.format(dateOptions.basicWithHours),
            rangeString: LAST_6_MONTHS,
            audiences: [],
        });
    };

    const onToDateChange = (date: Moment | null) => {
        setCustomEnd(date);
        let endDate: Moment;
        let startDate: Moment;

        if (date) {
            endDate = moment(date);
            startDate = endDate.clone();
            switch (selectedCustomRangeValue) {
                case CustomRangeFilterOptions[INCLUDE_3_MONTHS]:
                    startDate = endDate.clone().subtract(2, "months").startOf("month");
                    break;
                case CustomRangeFilterOptions[INCLUDE_6_MONTHS]:
                    startDate = endDate.clone().subtract(5, "months").startOf("month");
                    break;
                case CustomRangeFilterOptions[INCLUDE_12_MONTHS]:
                    startDate = endDate.clone().subtract(11, "months").startOf("month");
                    break;
            }
        } else {
            endDate = moment(new Date());
            startDate = endDate.clone().subtract(5, "months");
        }

        onChangeFilters({
            startDate: startDate.format(dateOptions.basicWithHours),
            endDate: endDate.format(dateOptions.basicWithHours),
        });
    };

    const onHandleAllAudience = (audienceSelected: boolean) => {
        let newFilters = { ...props.filters };

        if (audienceSelected) {
            newFilters.audiences = [];
        } else {
            let currentAudiences = props.filters.audiences ?? [];
            let allAudiences = props.audiences;
            allAudiences.forEach((audience: Audience) => {
                if (!currentAudiences.includes(audience.id)) {
                    currentAudiences.push(audience.id);
                }
            });
            newFilters.audiences = currentAudiences;
        }
        props.onChangeFilters(newFilters);

        setAudienceFilterText(newFilters.audiences);
    };

    const onClearAudience = (audience: string) => {
        const newAudiences = props.filters.audiences!.filter((selectedAudience) => selectedAudience !== audience);
        props.onChangeFilters({ audiences: newAudiences });
        setAudienceFilterText(newAudiences);
    };

    const setAudienceFilterText = (audiences?: string[]) => {
        if (audiences != null && audiences.length > 0) {
            setAudienceDropdownText(FILTER_BY_CUSTOM_AUDIENCES);
        } else {
            setAudienceDropdownText(FILTER_BY_AUDIENCES);
        }
    };

    const onChangeAudience = (currentAudience: string) => {
        const hasSelectedTag: boolean = !!props.filters.audiences?.find((selectedAudience) => selectedAudience === currentAudience);
        let newAudiences: string[];

        if (hasSelectedTag) {
            newAudiences = props.filters.audiences!.filter((selectedAudience) => selectedAudience !== currentAudience);
        } else {
            newAudiences = props.filters.audiences!.concat([currentAudience]);
        }

        props.onChangeFilters({ audiences: newAudiences });
        setAudienceFilterText(newAudiences);
    };

    const onUpdateAudienceTextToSearch = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setAudienceSearchText(ev.target.value);
    };

    /*
    * Returns the date range selection filter. Used for the static date filter.
    */
    const getDateRangeByOption = (valueToSet: FilterOptions) => {
        setSelectedValue(valueToSet);

        let startDateToSet = moment();
        let endDatetoSet = moment();

        switch (valueToSet) {
            case FilterOptions[LAST_3_MONTHS]:
                startDateToSet = moment(new Date()).subtract(2, "month").startOf("month").startOf("day");
                endDatetoSet = moment(new Date()).endOf("day").endOf("month");
                break;
            case FilterOptions[LAST_6_MONTHS]:
                startDateToSet = moment(new Date()).subtract(5, "month").startOf("month").startOf("day");
                endDatetoSet = moment(new Date()).endOf("day").endOf("month");
                break;
            case FilterOptions[LAST_12_MONTHS]:
                startDateToSet = moment(new Date()).subtract(11, "month").startOf("month").startOf("day");
                endDatetoSet = moment(new Date()).endOf("day").endOf("month");
                break;
            case FilterOptions[CUSTOM_RANGE]:
                includeDateRangeUpTo(CustomRangeFilterOptions[INCLUDE_3_MONTHS], customEnd);
                return;
        }

        onToDateChange(endDatetoSet);
        onChangeFilters({
            startDate: startDateToSet.format(dateOptions.basicWithHours),
            endDate: endDatetoSet.format(dateOptions.basicWithHours),
            rangeString: FilterOptions[valueToSet],
        });
    };

    /*
    * Includes the date range up to the selected value. Used for the custom range filter.
    */
    const includeDateRangeUpTo = (valueToSet: CustomRangeFilterOptions, dateToFilter: moment.Moment | null) => {
        setSelectedCustomRangeValue(valueToSet);

        let endDatetoSet = dateToFilter ? dateToFilter.clone().endOf("month") : moment().endOf("month");
        let startDateToSet = moment();

        switch (valueToSet) {
            case CustomRangeFilterOptions[INCLUDE_3_MONTHS]:
                startDateToSet = endDatetoSet.clone().subtract(2, "months").startOf("month");
                break;
            case CustomRangeFilterOptions[INCLUDE_6_MONTHS]:
                startDateToSet = endDatetoSet.clone().subtract(5, "months").startOf("month");
                break;
            case CustomRangeFilterOptions[INCLUDE_12_MONTHS]:
                startDateToSet = endDatetoSet.clone().subtract(11, "months").startOf("month");
                break;
        }

        onToDateChange(endDatetoSet);
        onChangeFilters({ 
            startDate: startDateToSet.format(dateOptions.basicWithHours), 
            endDate: endDatetoSet.format(dateOptions.basicWithHours), 
            rangeString: CustomRangeFilterOptions[valueToSet] 
        });
    };

    /*     
    * Returns the filters to be displayed in the filter bar.
    */
    const getFilters = () => (
        <div className="filters-display">
            <div>
                <DateRangeSelectFilter
                    options={dateOptionsMenu}
                    selectedValue={selectedValue}
                    onSelect={(value) => getDateRangeByOption(value)}
                />
            </div>
            {selectedValue === FilterOptions[CUSTOM_RANGE] && (
                <CustomDateRangeFilter
                    options={customDateOptionsMenu}
                    selectedValue={selectedCustomRangeValue}
                    selectedDate={customEnd ?? moment(new Date())}
                    onDateChange={onToDateChange}
                    onSelect={(value) => includeDateRangeUpTo(value, customEnd)}
                />
            )}
            <div>
                <AudienceFilters
                    filters={props.filters}
                    renameEveryoneInternalAudience="All users (Default)"
                    audienceDropdownText={audienceDropdownText}
                    audiences={filteredAudiences}
                    audienceSearchText={audienceSearchText}
                    onChangeAudience={onChangeAudience}
                    onHandleAllAudience={onHandleAllAudience}
                    onUpdateAudienceTextToSearch={onUpdateAudienceTextToSearch}
                />
            </div>
            <div style={{ marginLeft: "auto" }}>
                <Button
                    color="primary"
                    onClick={onClearFilters}
                    style={{ marginLeft: "10px" }}
                    disabled={selectedValue === FilterOptions[LAST_6_MONTHS] && (props.filters.audiences?.length ?? 0) === 0 && audienceSearchText === ""}
                >
                    Reset
                </Button>
            </div>
        </div>
    );

    return (
        <FilterContainer
            filters={getFilters()}
            filterCommands={<></>}
            filterSelection={<FilterSelection filters={props.filters} audiences={props.audiences} onClearAudience={onClearAudience} />}
        />
    );
};

interface AudienceAnalyticsFilterBarProps {
    filters: Partial<AudienceStatsFilterv1>;
    onChangeFilters: (filters: Partial<AudienceStatsFilterv1>) => void;
    isLoading: boolean;
    audiences: Audience[];
}

export default AudienceAnalyticsFilterBar;
